/**
 * BirthdayCelebration — Multi-section birthday invitation template.
 *
 * Deep blue theme with floating balloons, alternating photo trio,
 * animated happy-members grid, and a vertical activity timeline.
 *
 * Sections:
 *   Hero → Photo Trio → Happy Members → Schedule → RSVP/Venue
 *
 * Custom data shape (via InvitationData.customData.birthday):
 *   birthdayPersonName: string
 *   happyMembers: Array<{ name, photoUrl?, emoji? }>
 *   schedule: Array<{ time, activity, venue?, description?, emoji? }>
 *   venueAddress?: string
 *   mapsLink?: string
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { bdayTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { PhotoTrioSection } from './sections/PhotoTrioSection';
import { HappyMembersSection } from './sections/HappyMembersSection';
import { ScheduleSection } from './sections/ScheduleSection';
import { RsvpSection } from './sections/RsvpSection';

const META: TemplateMetadata = {
  id: 'birthday-celebration',
  name: 'Birthday Celebration',
  occasionType: 'BIRTHDAY',
  description:
    'Deep blue multi-section birthday invitation: floating balloons, trio photo layout, happy celebrants grid, and a vertical activity timeline.',
  isPremium: true,
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration'],
  colorScheme: {
    primary: bdayTheme.accent,
    accent: bdayTheme.accentSoft,
    bg: bdayTheme.bgDeep,
  },
};

export function BirthdayCelebration({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <main
      className="min-h-screen scroll-smooth font-sans antialiased"
      style={{
        ...themeCssVars(bdayTheme),
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <HeroSection content={content.hero} />
      <PhotoTrioSection content={content.photoTrio} />
      <HappyMembersSection content={content.happyMembers} />
      <ScheduleSection content={content.schedule} />
      <RsvpSection venue={content.venue} rsvp={content.rsvp} invitationId={invitationId} isLive={isLive} />
    </main>
  );
}

registry.register({ metadata: META, component: BirthdayCelebration });
